<!-- 📁 src/components/CompetitionExtraInfo.vue -->
<template>
  <div class="competition-info mb-2">
    <InfoBox
        icon="fa-solid fa-money-bill-wave"
        :label="$t('competitions.view.fees')"
      :value="formatFee(competition.competitions_info?.fees)"
    />
    <div class="prize-wrapper" @click="togglePrizeDetails" role="button" tabindex="0">
      <InfoBox
          icon="fa-solid fa-trophy"
          :label="$t('competitions.view.prize_amount')"
        :value="formatPrize(competition.competitions_info?.prize_amount)"
      />
      <div v-if="showPrizeDetails" class="prize-tooltip">
        <p class="prize-title">{{ $t('competitions.view.prize_breakdown') }}</p>
        <ul class="prize-list">
          <li v-for="item in prizeBreakdown" :key="item.label">
            <i :class="item.icon" aria-hidden="true"></i>
            <span>{{ item.label }}:</span>
            <strong>{{ item.value }}</strong>
          </li>
        </ul>
        <p v-if="!prizeBreakdown.length" class="prize-empty">{{ $t('competitions.view.not_defined') }}</p>
      </div>
    </div>
  </div>
  <div
      class="info-box-grid mb-2"
      :class="[`info-count-${infoNets.length}`]"
  >
    <InfoNet
        v-for="(item, index) in infoNets"
        :key="index"
        :icon="item.icon"
        :label="item.label"
        :value="item.value"
    />
  </div>
</template>

<script>
import InfoBox from "@/components/competitions/InfoBox.vue";
import InfoNet from "@/components/competitions/InfoNet.vue";
export default {
  name: "CompetitionExtraInfo",
  components: { InfoBox,InfoNet },
  props: {
    competition: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      showPrizeDetails: false
    };
  },
  computed: {
    currencyPrefix() {
      const candidates = [
        this.competition?.country?.currency_symbol,
        this.competition?.country?.prefix,
        this.competition?.competitions_info?.country?.currency_symbol,
        this.competition?.competitions_info?.country?.prefix,
        this.competition?.currency_symbol,
        this.competition?.currency?.symbol
      ];

      const found = candidates.find((value) => typeof value === 'string' && value.trim().length);
      return found ? found.trim() : '';
    },
    infoNets() {
      const info = this.competition.competitions_info || {};
      return [
        { label: this.$t('competitions.view.location'), icon: 'fa-solid fa-location-dot', value: this.formatExternalUrl(info.location) },
        { label: this.$t('competitions.view.url_event'), icon: 'fa-solid fa-link', value: this.formatExternalUrl(info.url_event) },
        { label: this.$t('competitions.view.url_social'), icon: 'fa-solid fa-share-nodes', value: this.formatExternalUrl(info.url_social) }
      ].filter(i => i.value);
    },
    prizeBreakdown() {
      const info = this.competition.competitions_info || {};
      const parts = [
        { key: 'prize_amount_first', label: this.$t('competitions.assistant.labels.prize_first'), icon: 'fa-solid fa-trophy' },
        { key: 'prize_amount_second', label: this.$t('competitions.assistant.labels.prize_second'), icon: 'fa-solid fa-medal' },
        { key: 'prize_amount_third', label: this.$t('competitions.assistant.labels.prize_third'), icon: 'fa-solid fa-medal' },
        { key: 'prize_amount_fourth', label: this.$t('competitions.assistant.labels.prize_fourth'), icon: 'fa-solid fa-award' }
      ];
      return parts
        .map(({ key, label, icon }) => {
          const raw = info[key];
          const num = Number(raw);
          if (!Number.isFinite(num) || num <= 0) return null;
          return { label, icon, value: this.formatCurrency(num, { withPrefix: true }) };
        })
        .filter(Boolean);
    }
  },
  methods: {
    togglePrizeDetails() {
      this.showPrizeDetails = !this.showPrizeDetails;
    },
    formatExternalUrl(value) {
      if (typeof value !== 'string') return value;
      const trimmed = value.trim();
      if (!trimmed) return null;
      if (/^[a-zA-Z][a-zA-Z\d+.-]*:/.test(trimmed)) return trimmed;
      return `https://${trimmed}`;
    },
    formatCurrency(value, { withPrefix = false } = {}) {
      const num = Number(value);
      if (!Number.isFinite(num)) return this.$t('competitions.view.not_defined');
      const rounded = Math.round(num);
      const formatted = rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      if (!withPrefix || !this.currencyPrefix) {
        return formatted;
      }

      return /[A-Za-z0-9]$/.test(this.currencyPrefix)
        ? `${this.currencyPrefix} ${formatted}`
        : `${this.currencyPrefix}${formatted}`;
    },
    formatPrize(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) return this.$t('competitions.view.not_defined');
      const rounded = Math.round(num);
      if (rounded === 0) return this.$t('competitions.assistant.labels.prize_zero_badge');
      return this.formatCurrency(rounded, { withPrefix: true });
    },
    formatFee(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) return this.$t('competitions.view.not_defined');
      const rounded = Math.round(num);
      if (rounded === 0) return this.$t('competitions.view.free');
      return this.formatCurrency(rounded, { withPrefix: true });
    }
  }

};
</script>

<style scoped>
.competition-info {
  display: flex;
  justify-content: space-around;
  margin-top: 15px;
}
.prize-wrapper {
  position: relative;
  width: 48%;
  display: flex;
}
.prize-wrapper .info-box {
  cursor: pointer;
  width: 100%;
}
.prize-tooltip {
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  background: #fff;
  border: 1px solid #ddd;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  padding: 10px 12px;
  min-width: 200px;
  z-index: 10;
}
.prize-title {
  margin: 0 0 6px;
  font-weight: 600;
  font-size: 13px;
}
.prize-list {
  list-style: none;
  padding: 0;
  margin: 0;
  font-size: 13px;
  text-align: left;
}
.prize-list li {
  display: grid;
  grid-template-columns: auto auto 1fr;
  align-items: center;
  gap: 6px;
}
.prize-list i {
  color: #c27a1a;
  font-size: 12px;
}
.prize-list li + li {
  margin-top: 4px;
}
.prize-empty {
  margin: 0;
  font-size: 12px;
  color: #666;
}
.info-box-grid {
  display: grid;
  gap: 12px;
  margin: 16px 0;
  justify-content: center; /* Centra los hijos si no se llenan las columnas */
}

/* Ajustes por cantidad */
.info-box-grid.info-count-1 {
  grid-template-columns: 1fr;
}

.info-box-grid.info-count-2 {
  grid-template-columns: repeat(2, 1fr);
}

.info-box-grid.info-count-3 {
  grid-template-columns: repeat(3, 1fr);
}


</style>
